/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Button,
  Text,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  InputGroup,
  InputRightElement,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Divider,
} from "@chakra-ui/react";
import { FiPlus, FiEdit2, FiRefreshCw, FiTool, FiShield, FiShoppingBag, FiCopy } from "react-icons/fi";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import { useUserRole } from "hooks/useUserRole";
import KullaniciContextMenu from "components/ContextMenu/KullaniciContextMenu";

export default function Yetkilendirme() {
  const toast = useToast();
  const { handleError } = useErrorHandler(); // Merkezi error handling
  const cancelRef = useRef();
  const [kullanicilar, setKullanicilar] = useState([]);
  const [loading, setLoading] = useState(false);
  const userRole = useUserRole();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedKullanici, setSelectedKullanici] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [contextMenu, setContextMenu] = useState({ position: null, kullanici: null });
  
  // Form states
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [formLoading, setFormLoading] = useState(false);

  // Rastgele şifre üret
  // Güvenli rastgele şifre oluştur (crypto API kullanarak)
  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    
    // Kriptografik olarak güvenli rastgele sayılar için crypto API kullan
    if (window.crypto && window.crypto.getRandomValues) {
      const randomValues = new Uint32Array(length);
      window.crypto.getRandomValues(randomValues);
      
      for (let i = 0; i < length; i++) {
        password += charset[randomValues[i] % charset.length];
      }
    } else {
      // Fallback: Eski tarayıcılar için (daha az güvenli ama yine de çalışır)
      for (let i = 0; i < length; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length));
      }
    }
    
    return password;
  };

  // Kullanıcı rolünü kontrol et
  useEffect(() => {
    // Sadece admin erişebilir
    if (userRole && userRole !== "admin") {
      toast({
        title: "Erişim Reddedildi",
        description: "Bu sayfaya sadece admin kullanıcılar erişebilir.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      window.location.href = "/admin/dashboard";
    }
  }, [userRole, toast]);

  // Kullanıcıları yükle
  const loadKullanicilar = useCallback(async () => {
    if (userRole !== "admin") return;
    
    setLoading(true);
    try {
      const response = await api.get("/api/yetkilendirme/kullanicilar");
      if (response.ok) {
        const data = await response.json();
        setKullanicilar(data);
      } else {
        throw new Error("Kullanıcılar yüklenemedi");
      }
    } catch (error) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(error, "Kullanıcılar yükleme hatası");
    } finally {
      setLoading(false);
    }
  }, [userRole, handleError]);

  useEffect(() => {
    if (userRole === "admin") {
      loadKullanicilar();
    }
  }, [userRole, loadKullanicilar]);

  // Yeni kullanıcı oluştur
  const handleCreate = async () => {
    // Şifre yoksa rastgele şifre üret
    let passwordToUse = formData.password;
    if (!passwordToUse) {
      passwordToUse = generateRandomPassword();
      setFormData({ ...formData, password: passwordToUse });
    }

    if (!formData.username || !passwordToUse) {
      toast({
        title: "Eksik Bilgi",
        description: "Kullanıcı adı gereklidir",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        username: formData.username,
        password: passwordToUse,
        role: "teknik_eleman", // Sadece teknik personel eklenebilir
      };

      const response = await api.post("/api/yetkilendirme/kullanicilar", payload);

      if (response.ok) {
        toast({
          title: "Başarılı",
          description: "Teknik personel başarıyla oluşturuldu",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsCreateOpen(false);
        setFormData({ username: "", password: "" });
        loadKullanicilar();
      } else {
        const error = await response.json();
        throw new Error(error.detail || "Teknik personel oluşturulamadı");
      }
    } catch (error) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(error, "Teknik personel oluşturma hatası");
    } finally {
      setFormLoading(false);
    }
  };

  // Kullanıcı güncelle
  const handleUpdate = async () => {
    if (!selectedKullanici) return;
    
    if (!formData.username) {
      toast({
        title: "Eksik Bilgi",
        description: "Kullanıcı adı gereklidir",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setFormLoading(true);
    try {
      const updateData = { username: formData.username };
      if (formData.password) {
        updateData.password = formData.password;
      }

      const response = await api.put(
        `/api/yetkilendirme/kullanicilar/${selectedKullanici.id}`,
        updateData
      );

      if (response.ok) {
        toast({
          title: "Başarılı",
          description: "Kullanıcı başarıyla güncellendi",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsEditOpen(false);
        setSelectedKullanici(null);
        setFormData({ username: "", password: "" });
        loadKullanicilar();
      } else {
        const error = await response.json();
        throw new Error(error.detail || "Kullanıcı güncellenemedi");
      }
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message || "Kullanıcı güncellenirken bir hata oluştu",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setFormLoading(false);
    }
  };

  // Kullanıcı sil
  const handleDelete = async () => {
    if (!selectedKullanici) return;

    setDeleteLoading(true);
    try {
      const response = await api.delete(
        `/api/yetkilendirme/kullanicilar/${selectedKullanici.id}`
      );

      if (response.ok || response.status === 204) {
        toast({
          title: "Başarılı",
          description: "Kullanıcı başarıyla silindi",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsDeleteOpen(false);
        setSelectedKullanici(null);
        loadKullanicilar();
      } else {
        const error = await response.json();
        throw new Error(error.detail || "Kullanıcı silinemedi");
      }
    } catch (error) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(error, "Kullanıcı silme hatası");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Edit modal aç
  const openEditModal = (kullanici) => {
    setSelectedKullanici(kullanici);
    setFormData({
      username: kullanici.username,
      password: "",
    });
    setIsEditOpen(true);
  };

  // Delete modal aç
  const openDeleteModal = (kullanici) => {
    setSelectedKullanici(kullanici);
    setIsDeleteOpen(true);
  };

  // Context menu işlemleri
  const handleContextMenu = (e, kullanici) => {
    e.preventDefault();
    // Admin kullanıcılar için context menü gösterme
    if (kullanici.role === "admin") return;
    setContextMenu({
      position: { x: e.clientX, y: e.clientY },
      kullanici: kullanici,
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ position: null, kullanici: null });
  };

  const textColor = useColorModeValue("gray.700", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const statsBg = useColorModeValue("gray.50", "navy.900");

  // Admin değilse sayfayı gösterme
  if (userRole !== "admin") {
    return (
      <Box p={4}>
        <Text>Yükleniyor...</Text>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex direction="column" gap="20px">
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap="20px">
          <VStack align="start" spacing={2}>
            <Text 
              fontSize={{ base: "xl", md: "2xl", lg: "3xl" }} 
              fontWeight="bold" 
              color="white"
              letterSpacing="-0.5px"
            >
              Yetkilendirme
            </Text>
            <Text 
              fontSize={{ base: "xs", md: "sm" }} 
              color="white"
              fontWeight="normal"
              opacity={0.9}
            >
              Sistem kullanıcılarını yönetin (Admin, Teknik Personel ve Bayiler).
            </Text>
          </VStack>
          <HStack>
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={loadKullanicilar}
              isLoading={loading}
              bg="white"
              color="blue.600"
              _hover={{ bg: "gray.50" }}
            >
              Yenile
            </Button>
            <Button
              leftIcon={<FiPlus />}
              onClick={() => {
                setFormData({ username: "", password: "" });
                setIsCreateOpen(true);
              }}
              colorScheme="blue"
            >
              Yeni Teknik Personel Ekle
            </Button>
          </HStack>
        </Flex>

        {/* Kullanıcılar Tablosu */}
        <Box bg={cardBg} borderRadius="15px" p="20px" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
          {loading ? (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="xl" />
            </Flex>
          ) : kullanicilar.length === 0 ? (
            <VStack py={12} spacing={4}>
              <Text textAlign="center" color="gray.500" fontSize="lg">
                Henüz kullanıcı bulunmamaktadır
              </Text>
              <Button
                leftIcon={<FiPlus />}
                onClick={() => {
                  setFormData({ username: "", password: "" });
                  setIsCreateOpen(true);
                }}
                colorScheme="blue"
                size="sm"
              >
                İlk Teknik Personeli Ekle
              </Button>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                  Kullanıcı Listesi ({kullanicilar.length})
                </Text>
              </Flex>
              <Divider />
              <Box overflowX="auto">
                <Table variant="simple" size="md">
                  <Thead>
                    <Tr bg={statsBg}>
                      <Th color={textColor} fontWeight="semibold">Kullanıcı Adı</Th>
                      <Th color={textColor} fontWeight="semibold">Rol</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {kullanicilar.map((kullanici) => (
                      <Tr 
                        key={kullanici.id} 
                        _hover={{ bg: statsBg }}
                        onContextMenu={(e) => handleContextMenu(e, kullanici)}
                        cursor={(kullanici.role === "teknik_eleman" || kullanici.role === "bayi") ? "context-menu" : "default"}
                      >
                        <Td>
                          <HStack spacing={2}>
                            {kullanici.role === "admin" && <FiShield size={16} color="gold" />}
                            {kullanici.role === "teknik_eleman" && <FiTool size={16} color="teal" />}
                            {kullanici.role === "bayi" && <FiShoppingBag size={16} color="blue" />}
                            <Text fontWeight="medium" color={textColor}>
                              {kullanici.username}
                            </Text>
                          </HStack>
                        </Td>
                        <Td>
                          <Badge 
                            colorScheme={
                              kullanici.role === "admin" ? "yellow" : 
                              kullanici.role === "teknik_eleman" ? "teal" : 
                              "blue"
                            } 
                            borderRadius="full" 
                            px={3} 
                            py={1}
                          >
                            {kullanici.role === "admin" ? "Süper Admin" : 
                             kullanici.role === "teknik_eleman" ? "Teknik Personel" : 
                             "Bayi"}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          )}
        </Box>
      </Flex>

      {/* Yeni Teknik Personel Ekle Modal */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={textColor}>Yeni Teknik Personel Ekle</ModalHeader>
          <ModalCloseButton />
          <Divider />
          <ModalBody py={6}>
            <VStack spacing={5}>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color={textColor}>Kullanıcı Adı</FormLabel>
                <Input
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  placeholder="Kullanıcı adı (min 5 karakter)"
                  size="md"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Sadece harf, rakam ve alt çizgi kullanılabilir
                </Text>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color={textColor}>Şifre</FormLabel>
                <InputGroup>
                  <Input
                    type="text"
                    value={formData.password || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder="Boş bırakılırsa otomatik oluşturulur"
                    size="md"
                    fontFamily="mono"
                    onFocus={() => {
                      // Şifre boşsa otomatik oluştur
                      if (!formData.password) {
                        const newPassword = generateRandomPassword();
                        setFormData({ ...formData, password: newPassword });
                      }
                    }}
                  />
                  <InputRightElement width="3rem">
                    <IconButton
                      h="1.75rem"
                      size="sm"
                      icon={<FiCopy />}
                      onClick={async () => {
                        const passwordToCopy = formData.password || generateRandomPassword();
                        if (passwordToCopy) {
                          try {
                            await navigator.clipboard.writeText(passwordToCopy);
                            toast({
                              title: "Kopyalandı",
                              description: "Şifre panoya kopyalandı",
                              status: "success",
                              duration: 2000,
                              isClosable: true,
                            });
                          } catch (err) {
                            // Merkezi error handling - Güvenli mesaj göster
                            handleError(err, "Şifre kopyalama hatası");
                          }
                        }
                      }}
                      aria-label="Şifreyi kopyala"
                    />
                  </InputRightElement>
                </InputGroup>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Boş bırakılırsa otomatik şifre oluşturulur. Kopyalamak için kopyala butonuna tıklayın.
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsCreateOpen(false)}
            >
              İptal
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreate}
              isLoading={formLoading}
              leftIcon={<FiPlus />}
            >
              Oluştur
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Kullanıcı Düzenle Modal */}
      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={textColor}>
            Teknik Personel Düzenle: {selectedKullanici?.username}
          </ModalHeader>
          <ModalCloseButton />
          <Divider />
          <ModalBody py={6}>
            <VStack spacing={5}>
              <FormControl isRequired>
                <FormLabel fontWeight="medium" color={textColor}>Kullanıcı Adı</FormLabel>
                <Input
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  placeholder="Kullanıcı adı"
                  size="md"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium" color={textColor}>Yeni Şifre (Opsiyonel)</FormLabel>
                <InputGroup>
                  <Input
                    type="text"
                    value={formData.password || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder="Değiştirmek istemiyorsanız boş bırakın"
                    size="md"
                    fontFamily="mono"
                    onFocus={() => {
                      // Şifre boşsa otomatik oluştur
                      if (!formData.password) {
                        const newPassword = generateRandomPassword();
                        setFormData({ ...formData, password: newPassword });
                      }
                    }}
                  />
                  <InputRightElement width="3rem">
                    <IconButton
                      h="1.75rem"
                      size="sm"
                      icon={<FiCopy />}
                      onClick={async () => {
                        const passwordToCopy = formData.password || generateRandomPassword();
                        if (passwordToCopy) {
                          try {
                            await navigator.clipboard.writeText(passwordToCopy);
                            toast({
                              title: "Kopyalandı",
                              description: "Şifre panoya kopyalandı",
                              status: "success",
                              duration: 2000,
                              isClosable: true,
                            });
                          } catch (err) {
                            // Merkezi error handling - Güvenli mesaj göster
                            handleError(err, "Şifre kopyalama hatası");
                          }
                        }
                      }}
                      aria-label="Şifreyi kopyala"
                    />
                  </InputRightElement>
                </InputGroup>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Şifre değiştirmek istemiyorsanız boş bırakın. Kopyalamak için kopyala butonuna tıklayın.
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsEditOpen(false)}
            >
              İptal
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdate}
              isLoading={formLoading}
              leftIcon={<FiEdit2 />}
            >
              Güncelle
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Kullanıcı Sil Onay Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Teknik Personel Sil
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text>
                <strong>{selectedKullanici?.username}</strong> adlı teknik personeli silmek
                istediğinizden emin misiniz?
              </Text>
              <Text mt={2} fontSize="sm" color="red.500">
                Bu işlem geri alınamaz. Teknik personel hesabı silinecektir.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setIsDeleteOpen(false)}
              >
                İptal
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                isLoading={deleteLoading}
                ml={3}
              >
                Sil
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Context Menu */}
      <KullaniciContextMenu
        position={contextMenu.position}
        data={contextMenu.kullanici}
        onClose={closeContextMenu}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />
    </Box>
  );
}

